import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/customer-ui/components/dialog";
import { OptionContent } from "@babackapp/customer-ui/components/option";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/customer-ui/components/select";
import { cn } from "@babackapp/customer-ui/lib/utils";
import { Loader2Icon } from "lucide-react";
import {
  type ComponentProps,
  type SyntheticEvent,
  Suspense,
  useState,
} from "react";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import {
  type OptionChoice,
  useVariantSelector,
} from "../../hooks/use-variant-selector";
import { shopifyThumbnail } from "../../utils/shopify-thumbnail";

/** Twice the rendered `size-8`, for dense screens. */
const ITEM_IMAGE_WIDTH = 64;

type VariantFooterProps = {
  orderId: string;
  lineItemId: string;
  shopId: string;
  onVariantChange: (variantId: string | null) => void;
  /** Variant already in the draft, kept when the customer comes back. */
  exchangeVariantId: string | null;
};

/**
 * Thumbnail of the selected variant. The spinner mounts only while loading and fades in past a short delay
 * (`starting:` runs the transition on mount too), so cached images don't flash it.
 * Rendered as the dialog trigger, so closing the zoom gives the focus back.
 */
function VariantThumbnail({
  src,
  ...buttonProps
}: { src: string | undefined } & ComponentProps<"button">) {
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const isLoading = loadedSrc !== src;

  if (!src) return null;

  const settle = (e: SyntheticEvent<HTMLImageElement>) => {
    if (e.currentTarget.currentSrc === e.currentTarget.src) setLoadedSrc(src);
  };

  return (
    <button
      type="button"
      {...buttonProps}
      className="relative size-12 shrink-0 cursor-zoom-in overflow-hidden rounded-md bg-muted outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
    >
      <img
        src={src}
        alt=""
        // A load or error queued for the previous image can run after `src`
        // changed: only the request for the current `src` ends the spinner.
        onLoad={settle}
        onError={settle}
        className="size-full object-cover"
      />
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center bg-muted/70 opacity-100 transition-opacity delay-150 starting:opacity-0">
          <Loader2Icon className="size-4 animate-spin text-muted-foreground" />
        </span>
      )}
    </button>
  );
}

type ChoiceSelectProps = {
  value: string;
  choices: (OptionChoice & { label?: string })[];
  locked: boolean;
  withImages: boolean;
  onChange: (value: string) => void;
};

/**
 * One select of the picker. With `withImages`, each item shows the image of
 * the variant it leads to: unlike a hover preview, it works on touch screens.
 *
 * A locked select stays focusable (`aria-disabled`, never opens) instead of
 * `disabled`: picking the last reachable value locks it in the same commit,
 * and Radix would hand the focus back to a disabled trigger, i.e. to `<body>`.
 * A select locked while open (shop refetch) is closed by the `open` prop alone,
 * which Radix does not report through `onOpenChange`: the open state is reset
 * here so it does not reopen by itself once unlocked.
 */
function ChoiceSelect({
  value,
  choices,
  locked,
  withImages,
  onChange,
}: ChoiceSelectProps) {
  const t = useCustomText();
  const [isOpen, setIsOpen] = useState(false);
  if (isOpen && locked) setIsOpen(false);
  const open = isOpen && !locked;
  return (
    <Select
      value={value}
      // Inside a form, Radix syncs a hidden native select that reports "" when
      // the value is not among its previous options (product switch): ignored.
      onValueChange={(v) => {
        if (v) onChange(v);
      }}
      open={open}
      onOpenChange={(next) => setIsOpen(next && !locked)}
    >
      <SelectTrigger
        aria-disabled={locked || undefined}
        className={cn("w-auto", locked && "cursor-not-allowed opacity-50")}
      >
        <SelectValue />
      </SelectTrigger>
      {/* Item-aligned (the default) lines the selected ItemText up with the
          trigger: an image before it would shift the list off to the left. */}
      <SelectContent position={withImages ? "popper" : "item-aligned"}>
        {choices.map((c) => (
          <SelectItem
            key={c.value}
            value={c.value}
            disabled={!c.available}
            leading={
              withImages &&
              (c.imageUrl ? (
                <img
                  src={shopifyThumbnail(c.imageUrl, ITEM_IMAGE_WIDTH)}
                  alt=""
                  loading="lazy"
                  className="size-8 shrink-0 rounded bg-muted object-cover"
                />
              ) : (
                <div className="size-8 shrink-0 rounded bg-muted" />
              ))
            }
          >
            {c.label ?? c.value}
            {!c.available && ` (${t("exchangeOptions.variant.unavailable")})`}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function VariantFooterInner({
  orderId,
  lineItemId,
  shopId,
  onVariantChange,
  exchangeVariantId,
}: VariantFooterProps) {
  const {
    productOption,
    selectedProductId,
    selectedOptions,
    selectedProduct,
    selectedImageUrl,
    selectableOptions,
    hasRealChoice,
    handleProductChange,
    handleOptionChange,
  } = useVariantSelector(
    orderId,
    lineItemId,
    shopId,
    onVariantChange,
    exchangeVariantId,
  );
  const t = useCustomText();

  if (!hasRealChoice) return null;

  const productTitle = selectedProduct?.productTitle ?? "";

  return (
    <OptionContent className="flex-wrap items-center">
      <Dialog>
        <DialogTrigger asChild>
          <VariantThumbnail
            // Full size, like the zoom: opening it then hits the cache.
            src={selectedImageUrl}
            aria-label={t("exchangeOptions.variant.zoomImage", {
              product: productTitle,
            })}
          />
        </DialogTrigger>
        {/* No corner X over the photo: the image itself closes the dialog,
            like the overlay and Escape. */}
        <DialogContent
          className="max-w-lg p-2"
          hideCloseButton
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">{productTitle}</DialogTitle>
          {selectedImageUrl && (
            <DialogClose
              className="cursor-zoom-out"
              aria-label={t("common.close")}
            >
              <img
                src={selectedImageUrl}
                alt={productTitle}
                className="w-full rounded-xl object-contain"
              />
            </DialogClose>
          )}
        </DialogContent>
      </Dialog>
      {productOption && (
        <ChoiceSelect
          value={selectedProductId}
          choices={productOption.choices}
          locked={productOption.locked}
          withImages={productOption.withImages}
          onChange={handleProductChange}
        />
      )}
      {selectableOptions.map((opt) => (
        <ChoiceSelect
          // Remounted per product: a select reused across products would
          // carry the previous product's values (see onValueChange above).
          key={`${selectedProductId}:${opt.name}`}
          value={selectedOptions[opt.name] ?? ""}
          choices={opt.choices}
          locked={opt.locked}
          withImages={opt.withImages}
          onChange={(v) => handleOptionChange(opt.name, v)}
        />
      ))}
    </OptionContent>
  );
}

export function VariantFooter(props: VariantFooterProps) {
  return (
    <Suspense fallback={null}>
      <VariantFooterInner {...props} />
    </Suspense>
  );
}
